
import { type QueryClient } from '@tanstack/vue-query'
import { Component, Vue, Prop } from 'vue-property-decorator'
import { ref, watch } from 'vue'

import LButton from '@/components/core/Button.vue'
import LDialog from '@/global-components/LDialog.vue'
import LocationSearch from '@/components/home/newTrip/LocationSearch.vue'
import OptionalTripForm from '@/components/home/newTrip/OptionalTripForm.vue'
import { tripModule, authModule, expenseModule } from '@/store'
import { useOnboardingStore } from '@/stores/onboarding'
import { User } from '@/types/user'
import { WaypointDataSource } from '@/utils/enums'
import { dateRange } from '@/utils/date-utils'
import { logEvent } from '@/utils/utility-manager'
import { tripQueryKeys } from '@/composables/trip'
import { useQueryClient } from '@/composables/vue-query'

@Component({
  name: 'NewTripModal',
  components: {
    LocationSearch,
    OptionalTripForm,
    LButton,
  },
  setup() {
    const queryClient = useQueryClient()
    const onboardingStore = useOnboardingStore()

    const isOpen = ref(false)
    const context = authModule.loggedIn ? 'signed_in' : 'onboarding_v3'

    watch(isOpen, (newValue) => {
      if (newValue) {
        logEvent('trip_creation_started', {
          context,
        })
      }
    })

    return {
      onboardingStore,
      queryClient,
      isOpen,
      context,
    }
  },
})
class NewTripModal extends Vue {
  @Prop({ type: Boolean, default: false }) disableAiHint!: boolean

  loading = false
  isSearching = false
  isSaving = false
  hasLocation = false
  hasName = false
  timestamp = new Date().getTime()
  onboardingStore!: ReturnType<typeof useOnboardingStore>
  queryClient!: QueryClient
  isOpen!: boolean
  context!: string

  trip = {
    name: '',
    attendees: authModule.loggedIn ? [this.user.id] : [],
    pendingAttendees: [] as string[],
    currency: 'EUR',
    date: {} as any,
  }

  get user(): Partial<User> {
    return authModule.user
  }

  public toggleDialog(): void {
    this.loading = false
    const dialog = this.$refs.newTripModal as LDialog
    dialog.toggleDialog()
  }

  setTimestamp(): void {
    this.timestamp = new Date().getTime()
  }

  localCurrency(): string {
    const baseCurrency = expenseModule.defaultCurrency.code
    const country = this.user.country?.code

    if (!country) {
      return baseCurrency
    }

    try {
      return (
        require('~/assets/json/country_currency_map.json')[country] ||
        baseCurrency
      )
    } catch (_) {
      return baseCurrency
    }
  }

  // Check for start- and enddate and create a new trip
  async createTrip() {
    const optionalTripForm = this.$refs.newTripOptionalForm as OptionalTripForm
    const locationOriginSearch = this.$refs
      .newTripOriginLocationSearch as LocationSearch
    const locationDestinationSearch = this.$refs
      .newTripDestinationLocationSearch as LocationSearch

    const { values, type } = optionalTripForm.getDates()
    const { invitees } = optionalTripForm.getInvitees()
    const selectedOrigin = locationOriginSearch.getSelection()
    const selectedDestination = locationDestinationSearch.getSelection()

    this.trip.date = dateRange({
      dates: values,
      type,
    })

    this.trip.name = selectedDestination.name
    this.trip.currency = this.localCurrency()

    // Get valid invitees (with either id or email)
    this.trip.pendingAttendees = invitees
      .map((invitee) => invitee.id ?? invitee.email)
      .filter((attendee): attendee is string => Boolean(attendee)) // removes any undefined/null/empty values

    const originLocation = this.convertSelectionToLocation(selectedOrigin)

    const destinationLocation =
      this.convertSelectionToLocation(selectedDestination)

    logEvent('create_trip_clicked', {
      context: authModule.loggedIn ? 'signed_in' : 'onboarding_v3',
    })

    if (!authModule.loggedIn || authModule.user.accountType === 'anonymous') {
      this.onboardingStore.setPendingTrip({
        ...this.trip,
        origin: originLocation,
        destination: destinationLocation,
      })
      this.$router.push({
        path:
          this.onboardingStore.previousAuthenticatingRoutePath ?? '/sign-up',
        query: { newTrip: '1' },
      })
      this.toggleDialog()
      return
    }

    this.loading = true
    await tripModule.create({
      trip: this.trip,
      location: destinationLocation,
      origin: originLocation,
    })

    logEvent('created_trip', { context: 'signed_in' })
    await this.queryClient.invalidateQueries({
      queryKey: tripQueryKeys.lists(),
    })

    this.toggleDialog()

    if (tripModule.trip) {
      this.$emit('created', tripModule.trip)
    }
  }

  convertSelectionToLocation(
    selection: ReturnType<InstanceType<typeof LocationSearch>['getSelection']>,
  ) {
    let location: { place: string; dataSource: string | number } | undefined = {
      place: '',
      dataSource: '',
    }

    if (selection.inspiration) {
      location.place = selection.inspiration._id
      location.dataSource = 'LAMBUS'
    } else if (selection.place) {
      const matchedSource = Object.entries(WaypointDataSource).find(
        (entry) => entry[0] === selection.place!.dataSource,
      )

      if (matchedSource) {
        location.place = selection.place.id
        location.dataSource = matchedSource[1]
      }
    } else {
      location = undefined
    }

    return location
  }

  onHasOriginLocation(hasLocation: boolean) {
    if (hasLocation) {
      logEvent('origin_selected', {
        context: this.context,
      })
    }
  }

  onHasDestinationLocation(hasLocation: boolean) {
    this.hasLocation = hasLocation

    if (hasLocation) {
      logEvent('destination_selected', {
        context: this.context,
      })
    }
  }

  trackAIAssistantClick() {
    logEvent('ai_assistant_viewed', {
      context: this.context,
      source: 'trip_creation',
    })
  }
}
export default NewTripModal
